import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Dispatch,
  Fragment,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SuccessAlertContext } from "App";
import Notify from "config/notify";
import { OrderCheck, PlaceOrder } from "../types";
import TableCellSmall, {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "./TableCellSmall";
import { localeStringDisZero } from "../utils/common";
import Mask from "./Mask";

const limit = 10;

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    // textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    // textAlign: "center",
    p: 0.5,
    top: 57,
  },
  supplierUnderBottomCol: {
    // textAlign: "center",
    p: 0.5,
    top: 91,
  },
};

type Props = {
  open: boolean;
  title: string;
  content: string;
  ActionButtons: JSX.Element;
  onClose: Function;
  orderList: PlaceOrder[];
  isFastLoading?: MutableRefObject<boolean>;
  getMoreStockList?: Function;
  setInit?: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
  isMoreLoading?: boolean;
  error?: string;
  errorTitle?: string;
};
/**
 * 注文依頼CSV更新確認モーダルのテーブル
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function OrderConfirmTable({
  open,
  title,
  content,
  ActionButtons,
  onClose,
  orderList,
  isFastLoading,
  getMoreStockList,
  setInit,
  isLoading,
  isMoreLoading,
  error,
  errorTitle,
}: Props) {
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false);
  const [adjustment, setAdjustment] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const innerRef = useRef<HTMLDivElement | undefined>(undefined);
  const { showAlert } = useContext(SuccessAlertContext);

  // スクロール処理
  const handleScroll = async () => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading?.current || !contentRef.current) {
      return;
    }
    const scrollTop = contentRef.current?.scrollTop;
    const contentHeight = contentRef.current?.clientHeight;
    const scrollHeight = contentRef.current?.scrollHeight;
    if (scrollTop + contentHeight + 10 >= scrollHeight) {
      // eslint-disable-next-line no-param-reassign
      if (isFastLoading) isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      if (getMoreStockList) await getMoreStockList();
    })();
  }, [fireMoreLoadEvent]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (setInit) {
      setInit(true);
      // if (error) showAlert(error, "error");
      if (error) {
        Notify?.setNotification?.({
          show: true,
          title: "CSVアップロードでエラーがあります。",
          message: error,
        });
      }
    }
  }, [open]);

  // 表示数がlimitの時、中のコンテンツが小さいと無限ローディングさせることができないため、少しスクロールできるようにする。
  useEffect(() => {
    if (orderList.length === limit && !isMoreLoading) {
      // console.log('adjustment', adjustment, orderList.length, contentRef.current?.clientHeight);
      setTimeout(() => {
        if (
          contentRef.current &&
          innerRef.current &&
          contentRef.current.clientHeight - innerRef.current.clientHeight >= 0
        ) {
          // 中のコンテンツが小さい場合、調整オブジェクトを挿入
          setAdjustment(contentRef.current.clientHeight - innerRef.current.clientHeight + 1);
        } else {
          setAdjustment(undefined);
        }
      }, 0);
    } else {
      setAdjustment(undefined);
    }
  }, [orderList, isMoreLoading]);
  return (
    <Dialog open={open} onClose={() => onClose()} fullScreen>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} onScroll={handleScroll}>
        <Box ref={innerRef}>
          <DialogContentText>{error ? errorTitle : content}</DialogContentText>
          <TableContainer
            component={Paper}
            sx={{ overflowX: "initial", width: "fit-content", minWidth: "100%" }}
          >
            <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="order table">
              {orderList.length !== 0 && (
                <>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        得意先コード
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        商品コード
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        JANコード
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        仕入先名
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        ブランド
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        商品名
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        サイズ
                      </TableHeaderCellNarrow>
                      <TableHeaderCellWide sx={styles.supplierTopCol}>品番</TableHeaderCellWide>
                      <TableHeaderCellWide isTwoHeight right sx={styles.supplierTopCol}>
                        数量
                      </TableHeaderCellWide>
                      <TableHeaderCellNarrow isTwoHeight right sx={styles.supplierTopCol}>
                        本部原単価
                        {/* <br />
                        (単価) */}
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight right sx={styles.supplierTopCol}>
                        仕切単価
                        {/* <br />
                        (単価) */}
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight right sx={styles.supplierTopCol}>
                        店舗売単価
                        {/* <br />
                        (単価) */}
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        納品希望日
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        注文書作成者
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        注文書承認者
                      </TableHeaderCellNarrow>
                      {error && <TableHeaderCellSmall rowSpan={3}>エラー内容</TableHeaderCellSmall>}
                    </TableRow>
                    <TableRow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol} colSpan={6}>
                        発送先情報
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol} rowSpan={2}>
                        得意先伝票No.
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol} rowSpan={2}>
                        直送
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol} rowSpan={2}>
                        客注
                      </TableHeaderCellNarrow>
                      <TableHeaderCellWide sx={styles.supplierBottomCol} colSpan={3} rowSpan={2}>
                        店舗備考
                      </TableHeaderCellWide>
                      <TableHeaderCellWide sx={styles.supplierBottomCol} colSpan={3} rowSpan={2}>
                        本部備考
                      </TableHeaderCellWide>
                    </TableRow>
                    <TableRow>
                      <TableHeaderCellNarrow sx={styles.supplierUnderBottomCol}>
                        郵便番号
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierUnderBottomCol} colSpan={2}>
                        住所
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierUnderBottomCol}>
                        発送先名
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierUnderBottomCol}>
                        電話番号
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierUnderBottomCol}>
                        発送先コード
                      </TableHeaderCellNarrow>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {orderList.map((order, index) => {
                      const isZebraStripeRow = !(error && order.error) ? (index % 2 === 1) : false;
                      return (
                      <>
                        <TableRow
                          key={order.id}
                          sx={(error && order.error) ? { backgroundColor: "var(--red-400)" } : {}}
                        >
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.clientCd || order.storeCd}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.productCd}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.janCd}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.supplierName}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.brand}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.productName}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.size}</TableCellNarrow>
                          <TableCellWide multi gray={isZebraStripeRow}>{order.productNumber}</TableCellWide>
                          <TableCellWide right multi gray={isZebraStripeRow}>{localeStringDisZero(order.orderCount)}</TableCellWide>
                          <TableCellNarrow right multi gray={isZebraStripeRow}>
                            {/* {localeStringDisZero(order.headquartersCost)}<br /> */}
                            {`${localeStringDisZero(order.headquartersUnitCost)}`}
                          </TableCellNarrow>
                          <TableCellNarrow right multi gray={isZebraStripeRow}>
                            {/* {localeStringDisZero(order.wholesalePrice)}<br /> */}
                            {`${localeStringDisZero(order.wholesaleUnitPrice)}`}
                          </TableCellNarrow>
                          <TableCellNarrow right multi gray={isZebraStripeRow}>
                            {/* {localeStringDisZero(order.shopPrice)}<br /> */}
                            {`${localeStringDisZero(order.shopUnitPrice)}`}
                          </TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.requestDeliveryDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.shopOrderer}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.approver}</TableCellNarrow>
                          {error &&
                            (order.error ? (
                              <TableCellWide rowSpan={2}>
                                <MultiTypography><>{
                                  // eslint-disable-next-line react/no-array-index-key
                                  order.error.split('\n').map((e,i) => (i === 0 ? e : <Fragment key={i}><br/>{e}</Fragment>))
                                }</></MultiTypography>
                              </TableCellWide>
                            ) : (
                              <TableCellWide rowSpan={2} />
                            ))}
                        </TableRow>
                        <TableRow
                          sx={(error && order.error) ? { backgroundColor: "var(--red-400)" } : {}}
                        >
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.postalCd}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow} colSpan={2}>{order.destinationAddress}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.destinationName}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.phoneNumber}</TableCellNarrow>
                          {/* <TableCellNarrow multi gray={isZebraStripeRow}>{order.faxNumber}</TableCellNarrow> */}
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.destinationCd}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.clientBillNumber}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.isDirectDelivery === true ? 'メーカー直送で納品' : order.isDirectDelivery === false ? '物流経由で納品' : '-'}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.isCustomerOrder ? '〇' : '×'}</TableCellNarrow>
                          <TableCellWide multi gray={isZebraStripeRow} colSpan={3}>{order.shopRemarks}</TableCellWide>
                          <TableCellWide multi gray={isZebraStripeRow} colSpan={3}>{order.headquartersRemarks}</TableCellWide>
                        </TableRow>
                      </>
                    )})}
                  </TableBody>
                </>
              )}
            </Table>
          </TableContainer>
          {adjustment && ( // 画面が大きいと無限スクロールが発火しないことがある
            <Box height={adjustment} />
          )}
          {isMoreLoading && (
            <Box py={3} textAlign="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>{ActionButtons}</DialogActions>
      <Mask isLoading={isLoading} />
    </Dialog>
  );
}
